/**
 * Adding a client, in one window: who the client is, then the administrator's
 * own password, then the invitation to send and what happens after it.
 *
 * The administrator's login sits in a hidden username field right before the
 * password. Without it the browser took the client's login field for the
 * username of the saved admin credentials and filled the admin's own login
 * into it (2026-10-13).
 */
import { useQueryClient } from "@tanstack/react-query";
import { Check, Copy, UserPlus, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { Button } from "@/design-system/Button";
import { TextField } from "@/design-system/TextField";
import { createPlatformClient } from "@/shared/api";
import { CLIENTS_KEY } from "@/shared/clients/directory";

/** What a refused creation means, by the server's answer — never a guess. */
const REFUSAL: Readonly<Record<number, string>> = {
  400: "Этот логин уже занят — клиентом или учётной записью администратора. Выбери другой, например email клиента.",
  403: "Пароль администратора не подошёл.",
  422: "Проверь поля: логин и название — до 128 символов.",
};

type Invitation = { readonly url: string; readonly login: string; readonly expiresAt: string };

function day(value: string): string {
  return new Date(value).toLocaleString("ru-RU", { dateStyle: "medium", timeStyle: "short" });
}

function InvitationLink({ invitation }: { readonly invitation: Invitation }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-4" data-testid="client-invitation">
      <p className="flex items-center gap-2 text-body-main font-medium text-status-positive">
        <Check aria-hidden="true" className="size-4" />
        Клиент создан
      </p>
      <div className="flex flex-col gap-2">
        <span className="text-sm text-text-secondary">Ссылка-приглашение</span>
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 break-all rounded-md border border-border-subtle bg-background-base px-3 py-2 text-data-mono">
            {invitation.url}
          </code>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              void navigator.clipboard.writeText(invitation.url).then(() => { setCopied(true); });
            }}
            aria-label="Скопировать ссылку приглашения"
          >
            {copied ? <Check aria-hidden="true" className="size-4" /> : <Copy aria-hidden="true" className="size-4" />}
            {copied ? "Скопировано" : "Скопировать"}
          </Button>
        </div>
        <p className="text-helper-sm text-text-muted">
          Одноразовая, действует до {day(invitation.expiresAt)}. Отправь лично, не в общий чат.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm text-text-secondary">Что дальше</span>
        <ol className="flex list-decimal flex-col gap-1 pl-5 text-body-main text-text-primary">
          <li>Клиент открывает ссылку и задаёт пароль. Логин: <span className="font-mono">{invitation.login}</span></li>
          <li>В кабинете подключает ключ Bybit — там есть пошаговая инструкция.</li>
          <li>Включает торговлю. Ход каждого шага видно в таблице клиентов.</li>
        </ol>
      </div>
    </div>
  );
}

export function AddClientDialog({
  adminLogin,
  onClose,
}: {
  readonly adminLogin: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const titleId = useId();
  const first = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<Invitation | null>(null);

  const panel = useRef<HTMLDivElement>(null);
  // The latest close handler without re-running the effect: the page re-renders
  // on every refetch, and re-focusing then moved the cursor mid-typing.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    first.current?.focus();
    const keys = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close.current();
        return;
      }
      if (event.key !== "Tab" || panel.current === null) return;
      // Focus stays inside the window while it is open.
      const focusable = [...panel.current.querySelectorAll<HTMLElement>("button, input:not([hidden]), [href]")]
        .filter((element) => !element.hasAttribute("disabled"));
      const start = focusable[0];
      const end = focusable[focusable.length - 1];
      if (start === undefined || end === undefined) return;
      if (event.shiftKey && document.activeElement === start) {
        event.preventDefault();
        end.focus();
      } else if (!event.shiftKey && document.activeElement === end) {
        event.preventDefault();
        start.focus();
      }
    };
    window.addEventListener("keydown", keys);
    return () => {
      window.removeEventListener("keydown", keys);
      opener?.focus();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="add-client-dialog"
        className="w-full max-w-lg rounded-lg border border-border-subtle bg-background-elevated shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <h2 id={titleId} className="flex items-center gap-2 text-body-lg font-semibold text-text-primary">
            <UserPlus aria-hidden="true" className="size-5 text-action-primary" />
            Новый клиент
          </h2>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Закрыть">
            <X aria-hidden="true" className="size-4" />
          </Button>
        </div>

        <div className="px-5 py-5">
          {invitation !== null ? (
            <>
              <InvitationLink invitation={invitation} />
              <div className="mt-6 flex justify-end">
                <Button type="button" onClick={onClose}>Готово</Button>
              </div>
            </>
          ) : (
            <form
              className="flex flex-col gap-6"
              onSubmit={(event) => {
                event.preventDefault();
                if (pending) return;
                const fields = new FormData(event.currentTarget);
                const raw = (name: string): string => {
                  const value = fields.get(name);
                  return typeof value === "string" ? value : "";
                };
                setPending(true);
                setRefusal(null);
                void createPlatformClient({
                  body: { display_name: raw("client-name").trim(), login: raw("client-login").trim(), current_password: raw("admin-password") },
                })
                  .then(async (result) => {
                    if (result.data === undefined) {
                      setRefusal((result.response === undefined ? undefined : REFUSAL[result.response.status]) ?? "Создание не подтверждено. Проверь список перед повтором.");
                      return;
                    }
                    setInvitation({
                      url: `${window.location.origin}/invite#token=${encodeURIComponent(result.data.token)}`,
                      login: result.data.login,
                      expiresAt: result.data.expires_at,
                    });
                    await queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
                  })
                  .catch(() => { setRefusal("Создание не подтверждено. Проверь список перед повтором."); })
                  .finally(() => { setPending(false); });
              }}
            >
              <fieldset className="flex flex-col gap-4">
                <legend className="mb-3 text-label-caps text-text-muted">1 · Кто клиент</legend>
                <TextField
                  ref={first}
                  label="Имя клиента"
                  name="client-name"
                  required
                  maxLength={128}
                  autoComplete="off"
                  placeholder="Иван Петров"
                  hint="Так клиент будет называться у тебя в списке."
                />
                <TextField
                  label="Логин клиента для входа"
                  name="client-login"
                  required
                  maxLength={128}
                  autoComplete="off"
                  inputMode="email"
                  spellCheck={false}
                  placeholder="ivan@mail.com"
                  hint="Удобнее всего — email клиента. Пароль он задаст сам по ссылке."
                />
              </fieldset>
              <fieldset className="flex flex-col gap-4">
                <legend className="mb-3 text-label-caps text-text-muted">2 · Подтверди, что это ты</legend>
                <input type="text" name="admin-login" autoComplete="username" value={adminLogin} readOnly hidden />
                <TextField
                  label="Твой пароль администратора"
                  name="admin-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  hint="Тот, с которым ты входишь в Skryptos. Клиенту он не передаётся."
                />
              </fieldset>
              {refusal !== null && <p role="alert" className="text-sm text-status-negative">{refusal}</p>}
              <div className="flex flex-wrap justify-end gap-2">
                <Button type="button" variant="ghost" onClick={onClose}>Отмена</Button>
                <Button type="submit" variant="primary" disabled={pending}>
                  {pending ? "Создаю…" : "Создать и получить приглашение"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
